import React, { useEffect, useRef } from 'react';
import { useEffectsStore } from '@/store/effects.store.ts';

interface WaveAnimationProps {
  className?: string;
  colors?: string[];
  amplitude?: number;
  frequency?: number;
  speed?: number;
}

const WaveAnimation: React.FC<WaveAnimationProps> = ({
  className = '',
  colors = ['rgba(59, 130, 246, 0.3)', 'rgba(139, 92, 246, 0.2)', 'rgba(16, 185, 129, 0.1)'],
  amplitude = 50,
  frequency = 0.02,
  speed = 0.02
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const timeRef = useRef(0);
  const { waveEffectsEnabled } = useEffectsStore();

  const drawWave = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    color: string,
    offset: number,
    amplitudeMultiplier: number = 1
  ) => {
    ctx.beginPath();
    ctx.moveTo(0, height);
    for (let x = 0; x <= width; x++) {
      const y = Math.sin((x * frequency) + offset) * amplitude * amplitudeMultiplier + height * 0.7;
      ctx.lineTo(x, y);
    }
    ctx.lineTo(width, height);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
  };

  const animate = () => {
    const canvas = canvasRef.current;
    if (!canvas || !waveEffectsEnabled) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { width, height } = canvas;
    ctx.clearRect(0, 0, width, height);
    colors.forEach((color, index) => {
      const offset = timeRef.current * speed + index * Math.PI * 0.5;
      const amplitudeMultiplier = 1 - index * 0.2;
      drawWave(ctx, width, height, color, offset, amplitudeMultiplier);
    });
    timeRef.current += 1;
    animationFrameRef.current = requestAnimationFrame(animate);
  };

  const handleResize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    canvas.style.width = '100vw';
    canvas.style.height = '100vh';
  };

  useEffect(() => {
    handleResize();
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (waveEffectsEnabled) animate();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  useEffect(() => {
    if (!waveEffectsEnabled && animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    } else if (waveEffectsEnabled && !animationFrameRef.current) {
      animate();
    }
  }, [waveEffectsEnabled]);

  if (!waveEffectsEnabled) return null;

  return (
    <canvas
      ref={canvasRef}
      className={`fixed inset-0 pointer-events-none z-50 w-full h-full overflow-hidden ${className}`}
      style={{ width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh' }}
    />
  );
};

export default WaveAnimation;
